import {
  REGENERATION_EMPTY_COMMENT_TEXT,
  buildRegenerationCommentSection,
} from '@mss_creator/shared';

/**
 * Keeping the drawer's Commentaire field and the revealed prompt in sync, in
 * both directions (issue #6).
 *
 * The two are two views of one thing: the comment is a section *inside* the
 * prompt, and once the prompt is editable the operator can be typing in either
 * place. Leaving them independent would mean the field says one thing, the
 * prompt another, and only one of them reaches the model — silently.
 *
 * The rule these functions exist to enforce is that nothing is ever written
 * blind. The section is found by its heading or not at all; when it is gone
 * (deleted or renamed by the operator), the field goes empty and disabled
 * rather than guessing where a section "should" go and re-inserting one. A
 * prompt is a single argument the model reads top to bottom — dropping a
 * paragraph back into it at a position nobody chose is worse than saying the
 * anchor is gone.
 */

/**
 * Matches the comment heading at any level, with either apostrophe and any
 * surrounding whitespace.
 *
 * Deliberately narrow: it recognises *this* heading, not any heading whose
 * text happens to mention a comment. "## Commentaires internes" is a section
 * the operator wrote, not the anchor, and treating it as one would put their
 * comment somewhere they never asked for. The tolerance that is here — heading
 * level, curly vs straight apostrophe, case, trailing spaces — covers what an
 * editor or a paste realistically changes without changing the meaning.
 */
const COMMENT_HEADING_PATTERN = /^[ \t]*#{1,6}[ \t]*Commentaire de l['’]utilisateur[ \t]*$/i;

/** Any other Markdown heading — where the comment section stops, when one follows it. */
const ANY_HEADING_PATTERN = /^[ \t]*#{1,6}[ \t]+\S/;

interface CommentSectionBounds {
  /** Index of the heading line. */
  headingLine: number;
  /** First line of the section body (may equal `endLine` for an empty section). */
  startLine: number;
  /** One past the last line of the section body. */
  endLine: number;
}

function findCommentSectionBounds(body: string): CommentSectionBounds | null {
  const lines = body.split('\n');
  const headingLine = lines.findIndex((line) => COMMENT_HEADING_PATTERN.test(line));
  if (headingLine === -1) return null;

  // The section body starts after the heading's blank separator line, if the
  // operator kept one, and runs to the next heading of any level or the end of
  // the editable text — "entre son titre et le titre suivant".
  let startLine = headingLine + 1;
  if (lines[startLine] === '') startLine += 1;

  let endLine = lines.length;
  for (let index = headingLine + 1; index < lines.length; index += 1) {
    const line = lines[index];
    if (line !== undefined && ANY_HEADING_PATTERN.test(line)) {
      endLine = index;
      break;
    }
  }
  // A heading immediately after ours leaves an empty section, and the blank
  // line we skipped belongs to the separation between the two.
  if (endLine < startLine) startLine = endLine;

  return { headingLine, startLine, endLine };
}

/** Whether the prompt still carries the anchor the Commentaire field writes into. */
export function hasCommentSection(body: string): boolean {
  return findCommentSectionBounds(body) !== null;
}

/**
 * The comment currently written in the prompt, or `null` when the section is
 * gone — the signal that empties and disables the field.
 *
 * The "no comment" placeholder reads back as an empty comment rather than as
 * its own literal text: the server writes that sentence *because* the field
 * was empty, so echoing it into the field would turn a placeholder into a
 * comment the user never typed, and the next keystroke would append to it.
 */
export function extractComment(body: string): string | null {
  const bounds = findCommentSectionBounds(body);
  if (!bounds) return null;

  const lines = body.split('\n');
  const section = lines.slice(bounds.startLine, bounds.endLine).join('\n').trim();
  return section === REGENERATION_EMPTY_COMMENT_TEXT ? '' : section;
}

/**
 * Rewrites the comment section's body in place, leaving everything around it
 * untouched. Returns `body` unchanged when the anchor is gone — the caller
 * disables the field in that case rather than re-inserting a section.
 */
export function replaceComment(body: string, comment: string): string {
  const bounds = findCommentSectionBounds(body);
  if (!bounds) return body;

  const lines = body.split('\n');
  // Rebuilt from the shared helper, so what the field writes and what the
  // server would have written are the same bytes — including the placeholder
  // when the field is empty.
  const rebuilt = buildRegenerationCommentSection(comment).split('\n');
  const trailing = lines.slice(bounds.endLine);
  // One blank line before whatever follows, matching how the assembled prompt
  // separates its sections. Nothing added at the very end of the text.
  const separator = trailing.length > 0 ? [''] : [];
  return [...lines.slice(0, bounds.headingLine), ...rebuilt, ...separator, ...trailing].join('\n');
}
